import React, { useEffect, useRef, useState } from "react";
import { FaFileArchive, FaFilePdf, FaWhatsapp, FaUpload, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { orderVerificationApi } from "../../api/varificationService";
import FullPageLoader from "../../common/FullPageLoader";

const MAX_DESIGN_FILES = 1;
const MAX_PROOF_FILES = 5;
const MAX_WHATSAPP_FILES = 1;

export default function DesignUploadForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const orderId =
    params?.orderId ||
    location?.state?.orderId ||
    new URLSearchParams(location.search).get("orderId") ||
    "";

  const [designFile, setDesignFile] = useState(null);

  const [finalDesignProof, setFinalDesignProof] = useState([]);

  const [proofPreviews, setProofPreviews] = useState({});

  const [whatsappScreenshot, setWhatsappScreenshot] = useState(null);

  const [whatsappPreview, setWhatsappPreview] = useState("");

  const [dragActive, setDragActive] = useState(null);

  const [fieldErrors, setFieldErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const designInputRef = useRef(null);

  const proofInputRef = useRef(null);

  const whatsappInputRef = useRef(null);
  const previewUrlsRef = useRef(new Set());

  const createPreviewUrl = (file) => {
    const url = URL.createObjectURL(file);
    previewUrlsRef.current.add(url);
    return url;
  };

  const revokePreviewUrl = (url) => {
    if (!url) return;
    URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(url);
  };

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    [],
  );

  const clearErrors = () => {
    setFieldErrors({});
  };

  const mapFieldErrors = (errors = {}) => {
    const mapped = {};
    Object.entries(errors).forEach(([field, message]) => {
      const normalizedField = field.toLowerCase().replace(/[^a-z]/g, "");
      const text = Array.isArray(message) ? message.join(", ") : String(message);

      if (/(designfile|designzip|zipfile)/.test(normalizedField)) {
        mapped.designFile = text;
      } else if (/(proof|finaldesign|^files\d*$)/.test(normalizedField)) {
        mapped.finalDesignProof = text;
      } else if (/(whatsapp|screenshot)/.test(normalizedField)) {
        mapped.whatsappScreenshot = text;
      }
    });
    return mapped;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) {
      return "0 KB";
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;

    return `${mb.toFixed(2)} MB`;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("File is required"));
        return;
      }

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Unable to convert file to Base64"));
          return;
        }

        resolve(reader.result);
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };
    });
  };

  const handleDesignFile = (file) => {
    clearErrors();

    if (!file) {
      return;
    }

    setDesignFile(file);
  };

  const handleProofFiles = (fileList) => {
    clearErrors();

    if (!fileList || fileList.length === 0) {
      return;
    }

    const selectedFiles = Array.from(fileList);

    const existingKeys = new Set(
      finalDesignProof.map((file) => `${file.name}-${file.size}-${file.lastModified}`),
    );
    const newFiles = selectedFiles.filter((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (existingKeys.has(key)) {
        return false;
      }
      existingKeys.add(key);
      return true;
    });
    const remainingSlots = MAX_PROOF_FILES - finalDesignProof.length;
    const filesToAdd = newFiles.slice(0, remainingSlots);
    const exceedsLimit = newFiles.length > remainingSlots;

    if (exceedsLimit) {
      setFieldErrors((previous) => ({
        ...previous,
        finalDesignProof: `You can upload up to ${MAX_PROOF_FILES} images or files.`,
      }));
    }

    const combinedFiles = [...finalDesignProof, ...filesToAdd];
    if (filesToAdd.length) {
      const newPreviews = {};
      filesToAdd.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (file.type.startsWith("image/")) newPreviews[key] = createPreviewUrl(file);
      });
      setProofPreviews((previous) => ({ ...previous, ...newPreviews }));
    }
    if (filesToAdd.length) {
      setFinalDesignProof(combinedFiles);
    }
  };

  const handleWhatsappFile = (file) => {
    clearErrors();

    if (!file) {
      return;
    }

    revokePreviewUrl(whatsappPreview);
    setWhatsappScreenshot(file);
    setWhatsappPreview(file.type.startsWith("image/") ? createPreviewUrl(file) : "");
  };

  const handleDragEnter = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSubmitting) {
      return;
    }

    setDragActive(type);
  };

  const handleDragOver = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSubmitting) {
      return;
    }

    setDragActive(type);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(null);
  };

  const handleDrop = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(null);

    if (isSubmitting) {
      return;
    }

    const files = event.dataTransfer.files;

    if (!files || files.length === 0) {
      return;
    }

    if (type === "design") {
      if (files.length > MAX_DESIGN_FILES) {
        clearErrors();
        setFieldErrors({ designFile: `You can upload up to ${MAX_DESIGN_FILES} image or file.` });
        return;
      }

      handleDesignFile(files[0]);

      return;
    }

    if (type === "proof") {
      handleProofFiles(files);

      return;
    }

    if (type === "whatsapp") {
      if (files.length > MAX_WHATSAPP_FILES) {
        clearErrors();
        setFieldErrors({
          whatsappScreenshot: `You can upload up to ${MAX_WHATSAPP_FILES} image or file.`,
        });
        return;
      }

      handleWhatsappFile(files[0]);
    }
  };

  const removeDesignFile = () => {
    setDesignFile(null);

    if (designInputRef.current) {
      designInputRef.current.value = "";
    }
  };

  const removeProofFile = (index) => {
    const removedFile = finalDesignProof[index];
    const key = removedFile
      ? `${removedFile.name}-${removedFile.size}-${removedFile.lastModified}`
      : "";
    revokePreviewUrl(proofPreviews[key]);
    setProofPreviews((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setFinalDesignProof((previousFiles) =>
      previousFiles.filter((_, fileIndex) => fileIndex !== index),
    );

    if (proofInputRef.current) {
      proofInputRef.current.value = "";
    }
  };

  const removeWhatsappFile = () => {
    revokePreviewUrl(whatsappPreview);
    setWhatsappPreview("");
    setWhatsappScreenshot(null);

    if (whatsappInputRef.current) {
      whatsappInputRef.current.value = "";
    }
  };

  const parseBackendError = (error) => {
    const responseData = error?.response?.data;

    console.log("Backend error response:", responseData);

    if (!responseData) {
      return {};
    }

    const backendError = responseData?.errors || responseData?.error;

    if (backendError && typeof backendError === "object" && !Array.isArray(backendError)) {
      const formattedErrors = {};

      Object.entries(backendError).forEach(([field, message]) => {
        formattedErrors[field] = Array.isArray(message) ? message.join(", ") : String(message);
      });

      return {
        errors: formattedErrors,
      };
    }

    return {};
  };

  const handleSubmit = async () => {
    clearErrors();

    if (!orderId) {
      setFieldErrors({});

      return;
    }

    try {
      setIsSubmitting(true);

      const designFileBase64 = designFile ? await fileToBase64(designFile) : "";

      const finalDesignProofBase64 = await Promise.all(
        finalDesignProof.map((file) => fileToBase64(file)),
      );

      const whatsappScreenshotBase64 = whatsappScreenshot
        ? await fileToBase64(whatsappScreenshot)
        : "";

      const payload = {
        orderId: orderId,

        design_file: designFileBase64,

        files: finalDesignProofBase64,

        screenShot: whatsappScreenshotBase64,
      };

      const response = await orderVerificationApi(payload);

      if (response?.success === true) {
        setFieldErrors({});

        navigate("/orders-list");

        return;
      }

      const responseErrors = response?.errors || response?.error;
      if (responseErrors && typeof responseErrors === "object" && !Array.isArray(responseErrors)) {
        setFieldErrors(mapFieldErrors(responseErrors));
      }
    } catch (error) {
      console.error("Order verification error:", error);

      const parsedError = parseBackendError(error);

      setFieldErrors(mapFieldErrors(parsedError.errors));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUploadBoxClass = (type) => {
    if (dragActive === type) {
      return "bg-primary-subtle border-primary";
    }

    return "bg-white border-secondary";
  };

  return (
    <div className="container-fluid py-4">
      {isSubmitting && <FullPageLoader text="Submitting for verification..." />}
      <div className="row">
        <div className="col-12">
          <div className="card border shadow-sm">
            <div className="card-body p-4">
              <div className="row g-4">
                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-dark text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaFileArchive size={22} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          Design File
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">Upload one image or file.</p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "design",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "design")}
                      onDragOver={(e) => handleDragOver(e, "design")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "design")}
                      onClick={() => {
                        if (!isSubmitting && designInputRef.current) {
                          designInputRef.current.click();
                        }
                      }}
                    >
                      <FaUpload size={30} className="text-secondary mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "design"
                          ? "Drop an image or file here"
                          : "Drag & Drop an image or file here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">
                        Select up to {MAX_DESIGN_FILES} image or file
                      </div>

                      <input
                        ref={designInputRef}
                        type="file"
                        className="d-none"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            handleDesignFile(file);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {fieldErrors.designFile && (
                      <div className="text-danger small mt-2" role="alert">
                        {fieldErrors.designFile}
                      </div>
                    )}

                    {/* SELECTED DESIGN FILE */}

                    {designFile && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center bg-dark text-white rounded-2"
                              style={{
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <FaFileArchive />
                            </div>

                            <div>
                              <div className="fw-semibold text-break">{designFile.name}</div>

                              <small className="text-muted">
                                {formatFileSize(designFile.size)}
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger remove-upload-file-button"
                            disabled={isSubmitting}
                            onClick={removeDesignFile}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    FINAL DESIGN PROOF
                ================================================= */}

                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaFilePdf size={22} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          Final Design Proof
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">
                          Upload up to {MAX_PROOF_FILES} images or files for the final approved
                          design proof.
                        </p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "proof",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "proof")}
                      onDragOver={(e) => handleDragOver(e, "proof")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "proof")}
                      onClick={() => {
                        if (
                          !isSubmitting &&
                          finalDesignProof.length < MAX_PROOF_FILES &&
                          proofInputRef.current
                        ) {
                          proofInputRef.current.click();
                        }
                      }}
                    >
                      <FaUpload size={30} className="text-secondary mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "proof"
                          ? "Drop files here"
                          : "Drag & Drop images or files here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">
                        {finalDesignProof.length >= MAX_PROOF_FILES
                          ? `Maximum of ${MAX_PROOF_FILES} files selected`
                          : `Select up to ${MAX_PROOF_FILES - finalDesignProof.length} more images or files`}
                      </div>

                      <input
                        ref={proofInputRef}
                        type="file"
                        multiple
                        className="d-none"
                        disabled={isSubmitting || finalDesignProof.length >= MAX_PROOF_FILES}
                        onChange={(e) => {
                          const files = e.target.files;

                          if (files?.length) {
                            handleProofFiles(files);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {fieldErrors.finalDesignProof && (
                      <div className="text-danger small mt-2" role="alert">
                        {fieldErrors.finalDesignProof}
                      </div>
                    )}

                    {/* FILE COUNT */}

                    {finalDesignProof.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                        <span className="fw-semibold">Selected Files</span>

                        <span className="badge bg-primary">{finalDesignProof.length}</span>
                      </div>
                    )}

                    {/* FILE LIST */}

                    {finalDesignProof.length > 0 && (
                      <div>
                        {finalDesignProof.map((file, index) => {
                          const previewKey = `${file.name}-${file.size}-${file.lastModified}`;
                          const previewUrl = proofPreviews[previewKey];
                          return (
                            <div
                              key={previewKey}
                              className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3 mb-2"
                            >
                              <div className="d-flex align-items-center gap-3">
                                {previewUrl ? (
                                  <img
                                    src={previewUrl}
                                    alt={`Preview of ${file.name}`}
                                    className="rounded-2 border"
                                    style={{ width: "64px", height: "64px", objectFit: "cover" }}
                                  />
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center bg-primary text-white rounded-2"
                                    style={{ width: "40px", height: "40px" }}
                                  >
                                    <FaFilePdf />
                                  </div>
                                )}

                                <div>
                                  <div className="fw-semibold text-break">{file.name}</div>

                                  <small className="text-muted">{formatFileSize(file.size)}</small>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger remove-upload-file-button"
                                disabled={isSubmitting}
                                onClick={() => removeProofFile(index)}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    WHATSAPP SCREENSHOT
                ================================================= */}

                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-success text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaWhatsapp size={24} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          WhatsApp Screenshot
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">
                          Upload one WhatsApp screenshot image or file.
                        </p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "whatsapp",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "whatsapp")}
                      onDragOver={(e) => handleDragOver(e, "whatsapp")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "whatsapp")}
                      onClick={() => {
                        if (!isSubmitting && whatsappInputRef.current) {
                          whatsappInputRef.current.click();
                        }
                      }}
                    >
                      <FaWhatsapp size={32} className="text-success mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "whatsapp"
                          ? "Drop an image or file here"
                          : "Drag & Drop an image or file here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">
                        Select up to {MAX_WHATSAPP_FILES} image or file
                      </div>

                      <input
                        ref={whatsappInputRef}
                        type="file"
                        className="d-none"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            handleWhatsappFile(file);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {fieldErrors.whatsappScreenshot && (
                      <div className="text-danger small mt-2" role="alert">
                        {fieldErrors.whatsappScreenshot}
                      </div>
                    )}

                    {/* SELECTED WHATSAPP FILE */}

                    {whatsappScreenshot && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3">
                          <div className="d-flex align-items-center gap-3">
                            {whatsappPreview ? (
                              <img
                                src={whatsappPreview}
                                alt={`Preview of ${whatsappScreenshot.name}`}
                                className="rounded-2 border"
                                style={{ width: "64px", height: "64px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center bg-success text-white rounded-2"
                                style={{ width: "40px", height: "40px" }}
                              >
                                <FaWhatsapp />
                              </div>
                            )}

                            <div>
                              <div className="fw-semibold text-break">
                                {whatsappScreenshot.name}
                              </div>

                              <small className="text-muted">
                                {formatFileSize(whatsappScreenshot.size)}
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger remove-upload-file-button"
                            disabled={isSubmitting}
                            onClick={removeWhatsappFile}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    SUBMIT
                ================================================= */}

                <div className="col-12">
                  <div className="border-top pt-4 mt-2 d-flex justify-content-end gap-2">
                    {/* SUBMIT */}

                    <button
                      type="button"
                      className="btn btn-primary px-4 d-inline-flex align-items-center justify-content-center text-nowrap"
                      style={{
                        minHeight: "38px",
                        width: "110px",
                      }}
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                            style={{
                              width: "1rem",
                              height: "1rem",
                            }}
                          />

                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <span>Not As Verified</span>
                        </>
                      )}
                    </button>

                    {/* CANCEL */}

                    <button
                      type="button"
                      className="btn"
                      style={{
                        backgroundColor: "#032B4D",
                        color: "#fff",
                        minHeight: "38px",
                      }}
                      disabled={isSubmitting}
                      onClick={() => navigate("/orders-list")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
